import { AppConst } from "constants/appConst";
import { useLayoutEffect, useRef, useState } from "react";
import Cropper, { Area, MediaSize } from "react-easy-crop";
import ReactModal from "react-modal";

/**
 * アイコン画像編集コンポーネント
 */
export default function FormImageCropper(props: {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  isReset: boolean;
  setIsReset: (isReset: boolean) => void;
  imageUrl: string | null;
  imageUrlDefault: string;
  cropWidth: number;
  cropRatio: number;
  displayWidth: number;
  setImageBlob: (blob: Blob) => void;
}): JSX.Element {
  /* ----------------------------------------------------------------------- */
  /* 型定義 */
  /* ----------------------------------------------------------------------- */

  /* ----------------------------------------------------------------------- */
  /* 変数定義 */
  /* ----------------------------------------------------------------------- */
  const inputFileRef = useRef(null);

  const [imgSrc, setImgSrc] = useState("");
  const [imgSrcCropped, setImgSrcCropped] = useState("");
  const [imgType, setImgType] = useState("");

  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [minZoom, setMinZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area>();

  const [isModalOpen, setIsModalOpen] = useState(false);

  /* ----------------------------------------------------------------------- */
  /* ライフサイクル関数定義 */
  /* ----------------------------------------------------------------------- */
  useLayoutEffect(() => {
    if (props.isOpen) {
      inputFileRef.current.click();
      props.setIsOpen(false);
    }
  }, [props.isOpen]);

  useLayoutEffect(() => {
    if (props.isReset) {
      props.setImageBlob(null);
    }
  }, [props.isReset]);

  useLayoutEffect(() => {
    if (props.imageUrl) {
      setImgSrcCropped(null);
    }
  }, [props.imageUrl]);

  /* ----------------------------------------------------------------------- */
  /* イベントハンドラ関数定義 */
  /* ----------------------------------------------------------------------- */
  /**
   * ファイル選択ダイアログで選択された画像を読み取ります。
   */
  const onImgFileInput = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      // ファイルタイプの検証
      const mimeType = e.target.files[0].type;

      if (AppConst.imageTypes.includes(mimeType)) {
        // ファイルの読み込み
        const reader = new FileReader();
        reader.addEventListener("load", () => {
          if (reader.result) {
            setImgSrc(reader.result.toString() || "");
            setIsModalOpen(true);
          }
        });
        reader.readAsDataURL(e.target.files[0]);

        // ファイル拡張子を判定するためのmimeTypeを取得
        setImgType(mimeType);
      }

      // 同じファイルを何度も指定できるように値を初期化
      e.target.value = "";
    }
  };

  /**
   * 画像編集画面初期表示時に、デフォルトの画像位置やzoom値を決定します。
   */
  const onMediaLoaded = (mediaSize: MediaSize) => {
    const { width, height } = mediaSize;
    const mediaAspectRadio = width / height;
    if (mediaAspectRadio > props.cropRatio) {
      // 縦幅に合わせてZoomを指定
      const result = props.cropWidth / props.cropRatio / height;
      setZoom(result);
      setMinZoom(result);
      return;
    }
    // 横幅に合わせてZoomを指定
    const result = props.cropWidth / width;
    setZoom(result);
    setMinZoom(result);
  };

  /**
   * 画像の位置調整や拡大/縮小時に切り取り位置を記録します。
   */
  const onCropComplete = (croppedArea: Area, croppedAreaPixels: Area) => {
    setCroppedAreaPixels(croppedAreaPixels);
  };

  /**
   * 画像編集確定時に、画像のバイナリをセットし表示画像を変更します。
   */
  const onComplete = async () => {
    if (!croppedAreaPixels) return;
    try {
      const blob = await _getCroppedImg(imgSrc, croppedAreaPixels);
      setImgSrcCropped(URL.createObjectURL(blob));

      props.setIsReset(false);
      props.setImageBlob(blob);
    } catch (e) {
      console.error(e);
    } finally {
      setIsModalOpen(false);
    }
  };
  /**
   * 画像編集をキャンセルします。
   */
  const onCancel = async () => {
    setIsModalOpen(false);
  };

  /* ----------------------------------------------------------------------- */
  /* プライベート関数定義 */
  /* ----------------------------------------------------------------------- */
  /**
   * urlをもとにimage要素を作成します。
   */
  const _createImage = (url: string): Promise<HTMLImageElement> =>
    new Promise((resolve, reject) => {
      const image = new Image();
      image.addEventListener("load", () => resolve(image));
      image.addEventListener("error", (error) => reject(error));
      // CodeSandboxでCORSエラーを回避するために必要
      image.setAttribute("crossOrigin", "anonymous");
      image.src = url;
    });

  /**
   * 画像トリミングを行います。
   */
  const _getCroppedImg = async (
    imageSrc: string,
    pixelCrop: Area
  ): Promise<Blob> => {
    const image = await _createImage(imageSrc);
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    if (!ctx) {
      return null;
    }

    const scale =
      pixelCrop.width > props.cropWidth ? props.cropWidth / pixelCrop.width : 1;

    // canvasサイズを設定
    canvas.width = image.width;
    canvas.height = image.height;

    ctx.scale(scale, scale);

    // canvas上に画像を描画
    ctx.drawImage(image, 0, 0);

    // トリミング後の画像を抽出
    const data = ctx.getImageData(
      pixelCrop.x * scale,
      pixelCrop.y * scale,
      pixelCrop.width * scale,
      pixelCrop.height * scale
    );

    // canvasのサイズ指定(切り取り後の画像サイズに更新)
    canvas.width = scale == 1 ? pixelCrop.width : props.cropWidth;
    canvas.height =
      scale == 1 ? pixelCrop.height : props.cropWidth / props.cropRatio;

    // 抽出した画像データをcanvasの左隅に貼り付け
    ctx.putImageData(data, 0, 0);

    // canvasを画像に変換
    return new Promise((resolve) => {
      canvas.toBlob((file) => {
        if (file !== null) {
          resolve(file);
        }
      }, imgType);
    });
  };

  /* ----------------------------------------------------------------------- */
  /* JSX */
  /* ----------------------------------------------------------------------- */
  return (
    <>
      {/* 表示画像 */}
      <img
        className="cropper-img"
        width={props.displayWidth}
        src={
          props.isReset
            ? props.imageUrlDefault
            : imgSrcCropped
            ? imgSrcCropped
            : props.imageUrl
            ? props.imageUrl
            : props.imageUrlDefault
        }
      />
      {/* ファイルアップローダー */}
      <input
        type="file"
        accept={AppConst.imageTypes.join(",")}
        name="user-image"
        id="user-image"
        ref={inputFileRef}
        onInput={onImgFileInput}
        style={{ display: "none" }}
      />
      {/* 画像編集モーダル */}
      <ReactModal
        isOpen={isModalOpen}
        onRequestClose={() => {
          setIsModalOpen(false);
        }}
        style={{
          overlay: {
            zIndex: 2000,
            backgroundColor: "rgba(30, 30, 30,0.5)",
          },
          content: {
            maxWidth: "600px",
            height: "600px",
          },
        }}
        className="ReactModal__Content__Custom"
        ariaHideApp={false}
      >
        <div className="modal modal-content rounded-4 modal-media">
          <div className="modal-header"></div>
          <div className="modal-body">
            <div className="d-md-flex d-flex-card">
              <div className="popImage">
                <Cropper
                  image={imgSrc}
                  crop={crop}
                  zoom={zoom}
                  minZoom={minZoom}
                  maxZoom={minZoom + 3}
                  aspect={props.cropRatio}
                  onCropChange={setCrop}
                  onCropComplete={onCropComplete}
                  onZoomChange={setZoom}
                  cropSize={{
                    width: props.cropWidth,
                    height: props.cropWidth / props.cropRatio,
                  }}
                  zoomSpeed={0.05}
                  classes={{
                    containerClassName: "container",
                    cropAreaClassName: "crop-area",
                    mediaClassName: "media",
                  }}
                  onMediaLoaded={onMediaLoaded}
                  showGrid={false}
                />
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <button
              onClick={onCancel}
              type="button"
              className="btn btn-card btn-card-lg gray"
            >
              キャンセル
            </button>
            <button
              onClick={onComplete}
              type="button"
              className="btn btn-card btn-card-lg moderate-blue"
            >
              この画像に決定
            </button>
          </div>
        </div>
      </ReactModal>
    </>
  );
}
